import { useMemo } from 'react';
import { CountrySettingsResponse } from '@core/api/useCountrySettings';
import { ArrayElement } from '@core/models';

import { useAppContext } from '../../App.context';
import { RequiredCountryId } from '@core/api/useSurveys';

const getCountryTitle = (country: ArrayElement<CountrySettingsResponse>): string => {
    return `${country.countryCode} - ${country.name}`;
};

const useCountryOptions = () => {
    const {
        countrySettings,
        isLoading: isCountrySettingsLoading
    } = useAppContext();

    const countryOptions = useMemo(
        () =>
            countrySettings.map((country) => ({
                id: country.id as RequiredCountryId,
                title: getCountryTitle(country),
            })),
        [countrySettings],
    );

    return { isCountrySettingsLoading, countryOptions };
};

export const useCountry = (countryId: number) => {
    const {
        countrySettings,
        isLoading
    } = useAppContext();

    const country = useMemo(() => {
        return !isLoading ? countrySettings.find((country) => country.id === countryId) : null;
    }, [isLoading, countryId, countrySettings])

    return {
        country,
        isLoading,
    };
}

export const useCountryName = (countryId: number) => {
    const {
        country,
        isLoading
    } = useCountry(countryId);

    return useMemo(() => {
        if (isLoading) {
            return 'Loading...';
        }

        return country ? getCountryTitle(country) : '';
    }, [country, isLoading]);
};

export default useCountryOptions;
